import { Currency } from "@redotech/money/iso4217/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Schema for the CHECKOUT_RATES_SHOWN customer event.
 *
 * Emitted server-side from Redo's carrier-service rates endpoint when rates
 * are returned to a shopper in Shopify checkout. Unlike the Shop+
 * CHECKOUT_INFO_CHANGED event (which originates from a Shopify UI extension
 * and carries a checkout token, cart value, customer identity, etc.), this
 * event is the only checkout-visit signal available for non-Shop-Plus
 * merchants — the carrier-service callback does not supply checkout token,
 * cart value, or customer identity.
 *
 * The visit is identified by an address fingerprint (hash of team + the
 * normalized shipping address). Conversion attribution happens in the
 * warehouse by joining this event to ORDER_CREATED on
 * (team_id, address_fingerprint, day-window).
 */
const rawCheckoutRatesShownEventSchema_ = z.object({
  /** Hash of (teamId + normalized shipping address). Same value emitted from the order webhook on completion for warehouse join. */
  addressFingerprint: z.string(),
  /** Treatment IDs the shopper was assigned for any active A/B tests at the time of the rates call. */
  assignedTreatmentIds: z.array(z.string()),
  /** Raw shipping address the rates were computed for. */
  shippingAddress: z.object({
    address1: z.string().nullish(),
    address2: z.string().nullish(),
    city: z.string().nullish(),
    province: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
  }),
  /** The shop's base currency (rates costs are denominated here). */
  shopCurrency: createEnum(Currency, { enumName: "Currency" }),
  /** The shipping options that were returned and will be shown to the shopper. */
  ratesShown: z.array(
    z.object({
      serviceCode: z.string(),
      serviceName: z.string(),
      cost: z.object({
        amount: z.number(),
        currency: createEnum(Currency, { enumName: "Currency" }),
      }),
    }),
  ),
  /**
   * Order totals from the carrier-service rates request (Shopify supplies
   * these in the `order_totals` field of the rate-callback body). Values are
   * in the rates-call currency (`shopCurrency`) and use the standard Shopify
   * units (currency major units, e.g. dollars). Nullish because the rates
   * endpoint sometimes runs before checkout has gathered enough cart data.
   */
  orderTotals: z
    .object({
      /** Cart subtotal before discounts. */
      subtotalPrice: z.number().nullish(),
      /** Cart total after discounts (excluding shipping). */
      totalPrice: z.number().nullish(),
      /** Discount amount applied to the cart. */
      discountAmount: z.number().nullish(),
    })
    .nullish(),
  /** Timestamp recorded server-side at rates-callback time. */
  localDateTime: z.object({ isoDateTime: z.coerce.date() }),
});

export const rawCheckoutRatesShownEventSchema: z.ZodType<RawCheckoutRatesShownEvent> =
  rawCheckoutRatesShownEventSchema_;

/**
 * Unboxed schema preserving the underlying object shape so consumers
 * (e.g. the persisted customer-event schema in customer-event-zod-schema.ts)
 * can compose via `.extend(rawCheckoutRatesShownEventSchemaShape.shape)`.
 */
export const rawCheckoutRatesShownEventSchemaShape =
  rawCheckoutRatesShownEventSchema_;

export interface RawCheckoutRatesShownEvent extends z.infer<
  typeof rawCheckoutRatesShownEventSchema_
> {}

const TWO_HOUR_INTERVAL_MS = 2 * 60 * 60 * 1000;

/**
 * Current two-hour interval identifier (UTC epoch milliseconds / 2h). Used to
 * bucket repeated carrier-service rates calls for the same address into a
 * single CHECKOUT_RATES_SHOWN customer event. `Date.prototype.getTime()` is
 * epoch milliseconds in UTC, so bucket boundaries are UTC-aligned.
 */
export function getTwoHourIntervalIdentifier(now: Date = new Date()): number {
  return Math.floor(now.getTime() / TWO_HOUR_INTERVAL_MS);
}

/**
 * Idempotency key for a CHECKOUT_RATES_SHOWN event. Repeated rates calls for
 * the same (team, address) within a two-hour window collapse onto one key.
 * Enqueue with `updateIfIdempotencyKeyMatch: true` so the event is upserted
 * (kept current with the latest rates) instead of dropped — mirroring how
 * CHECKOUT_PROBABILITY_CALCULATED upserts one event per session.
 *
 * Shared by the emit site (conversion rates endpoint) and the pipeline handler
 * so the key never diverges between the two.
 */
export function buildCheckoutRatesShownIdempotencyKey({
  teamId,
  addressFingerprint,
  now = new Date(),
}: {
  teamId: string;
  addressFingerprint: string;
  now?: Date;
}): string {
  return `checkout_rates_shown:${teamId}:${addressFingerprint}:${getTwoHourIntervalIdentifier(now)}`;
}
